import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { useAuth, RedirectIfAuthed } from "@/auth/AuthContext";
import LogoMark from "@/components/LogoMark";

const AdminLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) navigate("/admin", { replace: true });
    else setError(res.error);
  };

  return (
    <RedirectIfAuthed>
      <div className="min-h-screen bg-pine grain flex items-center justify-center px-5" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 w-full max-w-md bg-cream border border-line p-8 sm:p-10"
        >
          <div className="flex items-center gap-3 mb-8">
            <LogoMark size={44} />
            <div>
              <p className="font-serif text-lg font-semibold text-pine leading-tight">KEVANE</p>
              <p className="text-[10px] font-semibold tracking-[0.28em] uppercase text-gold-muted">
                Consulting &amp; Training
              </p>
            </div>
          </div>
          <h1 className="font-serif text-3xl font-medium text-pine">Site Admin</h1>
          <p className="mt-2 text-sm text-ink/60">Sign in to edit your website content.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="block text-xs font-semibold tracking-[0.2em] uppercase text-pine mb-2">
                Email
              </label>
              <input
                id="admin-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                className="w-full border border-line bg-white px-4 py-3 text-sm text-ink outline-none focus:border-gold transition-colors duration-300"
                autoComplete="email"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-xs font-semibold tracking-[0.2em] uppercase text-pine mb-2">
                Password
              </label>
              <input
                id="admin-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="w-full border border-line bg-white px-4 py-3 text-sm text-ink outline-none focus:border-gold transition-colors duration-300"
                autoComplete="current-password"
              />
            </div>
            {error && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-pine px-6 py-3.5 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-gold hover:text-pine disabled:opacity-60"
            >
              <Lock size={15} />
              {busy ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </motion.div>
      </div>
    </RedirectIfAuthed>
  );
};

export default AdminLogin;
